import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import MyDeskBuildBadge from '@/components/MyDeskBuildBadge';
import { ADMIN_TOOLS } from '@/data/navShellManifest';
import { renderNavIcon } from '@/components/layout/navIcon';
import { createPortal } from 'react-dom';
import { useHoverPanel } from '@/hooks/useHoverPanel';

/**
 * ADMIN TOOLS SQUARE — DP57 Part 4.
 *
 * The labelled Admin Tools block in the rail becomes one small square sitting
 * directly above the user avatar at the foot of the rail. All five destinations
 * stay; nothing was dropped or renamed.
 *
 * The hover mechanism is NOT a new pattern. It is the avatar menu's, read from
 * components/FixedBottomNav.js and reused exactly:
 *   - three phases: 'closed' | 'fading' | 'open'
 *   - openMenu on onMouseEnter and onFocus
 *   - startFade on onMouseLeave and onBlur
 *   - a 700ms fade timer, and during 'fading' the panel KEEPS pointer-events so
 *     the pointer can travel from the square into the menu without it shutting.
 *     FixedBottomNav's own comment warns that making the panel
 *     pointer-events-none while fading is "the easy way to get this wrong".
 *   - onClick PINS it open, which is also how a tap works on a touch device
 *   - the same panel styling: rounded-xl, border, backdrop-blur, /95 surfaces
 *
 * It looks and behaves identically whether the rail is open or collapsed: it is
 * one fixed icon, so it has no second state. Same breakpoint as the avatar
 * (`hidden md:flex`) so the two stay a pair.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_HELP_IDS = {
  financials: 'financials-page',
  team: 'team-management',
  'company-settings': 'company-settings',
  'admin-settings': 'admin-dashboard',
};

export const AdminToolsSquare = ({
  inline = false,
  darkMode,
  isAdmin,
  isFinancial,
  isSuperAdmin,
  iconAssignments,
  globalIconSettings,
}) => {
  const navigate = useNavigate();
  // DP60 Part 0d — the phase machine moved to hooks/useHoverPanel so the rail's
  // boxes and this square share ONE implementation instead of two copies of
  // FixedBottomNav's. Same phases, same 700ms, same pointer-events-during-fade.
  const hover = useHoverPanel();
  /* DP105 ruling 5 — THE PANEL IS PORTALLED OUT OF THE RAIL.
     It was `absolute bottom-0 left-[46px]` inside this wrap, and AppShell mounts
     this box `inline` in `.km-rail`'s foot — a stacking context at z 3000. So the
     panel could not rise above the drawer (4000) or anything else outside the
     rail, the same trap the nine rail tips were in (DP104 Part 2). Same fix:
     `createPortal` to the body, `position: fixed`, coordinates converted from
     the square's own rect and clamped, and `--km-z-tooltip` instead of a
     positional class. The hover machine (hooks/useHoverPanel) is untouched. */
  const btnRef = React.useRef(null);
  const [menuAt, setMenuAt] = React.useState(null);
  React.useEffect(() => {
    if (!hover.expanded && hover.phase !== 'fading') return;
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setMenuAt({
      left: Math.max(8, Math.min(r.right + 10, window.innerWidth - 216)),
      bottom: Math.max(8, window.innerHeight - r.bottom),
    });
  }, [hover.expanded, hover.phase]);
  const { phase, expanded, closeNow } = hover;

  if (!isAdmin) return null;

  const items = ADMIN_TOOLS.filter(item => !(item.financialOnly && !isFinancial));

  const go = (path) => { navigate(path); closeNow(); };

  return (
    <div
      // DP57 — bottom-[7.5rem] (120px), not the 86px that would sit 10px above
      // the avatar. Two fixed widgets already occupy that column and overlap
      // each other: the avatar (bottom-10, 40-76px) and GlobalDarkModeToggle
      // (bottom-[4.25rem] z-[9997], 68-108px). At 86px the square was covered by
      // the toggle, which has the higher z-index, and was not clickable at all.
      // 120px is the first clear band above the avatar. Reported, not hidden.
      //
      // DP59 — AI CHAD WINS, by ruling. This is back BELOW him (z-[9998] against
      // his 99999, AiChad.js:195). Instead of stacking over him the menu POSITIONS
      // clear: he is fixed at left:16, bottom:calc(33vh - 28px), so on a 950px
      // viewport he occupies roughly 285-345px from the bottom. The menu opens
      // UPWARD from the square at 120px and is ~250px tall, reaching ~370px — so
      // it is opened to the RIGHT of the rail instead, clear of his column
      // entirely. AI Chad was not touched.
      // DP60 Part 0c — `inline` puts this box inside the rail's fixed foot
      // (components/layout/AppShell.js) so the foot is one column at one size.
      // The standalone fixed position is kept for any caller that still wants it.
      className={inline
        ? 'relative flex flex-col items-start'
        : 'fixed bottom-[7.5rem] left-3 z-[var(--km-z-menu)] hidden md:flex flex-col items-start pointer-events-none'}
      data-testid="admin-tools-square-wrap"
    >
      {menuAt && createPortal(
      <div
        onMouseEnter={hover.open}
        onMouseLeave={hover.startFade}
        data-testid="admin-tools-menu"
        data-menu-phase={hover.dataPhase}
        style={{ position: 'fixed', left: menuAt.left, bottom: menuAt.bottom,
                 zIndex: 'var(--km-z-tooltip)' }}
        className={`flex flex-col origin-bottom-left transition-all ease-out ${
          expanded
            ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto duration-[120ms]'
            : phase === 'fading'
              ? 'opacity-0 translate-x-0 scale-100 pointer-events-auto duration-700'
              : 'opacity-0 -translate-x-3 scale-95 pointer-events-none duration-300'
        }`}
      >
        <div
          data-km-surface={darkMode ? 'dark' : undefined}
          className={`rounded-xl overflow-hidden shadow-xl border min-w-[200px] ${
            darkMode
              ? 'bg-[#141414]/95 border-[#2A2A2A] backdrop-blur-md'
              : 'bg-white/95 border-gray-200/80 backdrop-blur-md shadow-lg'
          }`}
        >
          <div className={`px-3 py-2 text-[9px] font-semibold uppercase tracking-wider flex items-center gap-1.5 border-b ${
            darkMode ? 'text-amber-500 border-[#2A2A2A]' : 'text-amber-600 border-gray-200/80'
          }`}>
            <Shield className="w-3 h-3" />
            Admin Tools
          </div>

          {items.map(item => (
            <button
              key={item.id}
              onClick={() => go(item.path)}
              data-testid={`admin-tools-${item.id}`}
              data-help-id={ADMIN_HELP_IDS[item.id] || `admin-${item.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 transition-colors ${
                darkMode
                  ? 'text-amber-400/70 hover:bg-amber-900/20 hover:text-amber-400'
                  : 'text-amber-700/70 hover:bg-amber-50 hover:text-amber-700'
              }`}
            >
              {renderNavIcon(item, iconAssignments, API, darkMode, false, globalIconSettings)}
              <span className="text-sm flex-1 text-left">{item.label}</span>
            </button>
          ))}

          {isSuperAdmin && (
            <button
              onClick={() => go('/super-admin')}
              data-testid="admin-tools-super-admin"
              className={`w-full flex items-center gap-3 px-3 py-2 transition-colors border-t ${
                darkMode
                  ? 'text-purple-400 hover:bg-purple-900/20 hover:text-purple-300 border-[#2A2A2A]'
                  : 'text-purple-600 hover:bg-purple-50 hover:text-purple-700 border-gray-200/80'
              }`}
            >
              <Shield className="w-5 h-5" />
              <span className="text-sm flex-1 text-left font-medium">Super Admin</span>
            </button>
          )}

          {/* The live build fingerprint. It used to sit under the Super Admin
              link inside the rail block; it moves here, still super-admin only,
              and no longer depends on whether the rail is collapsed. */}
          {isSuperAdmin && (
            <div className={`px-3 py-2 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200/80'}`}
                 data-testid="admin-tools-build-badge">
              <MyDeskBuildBadge />
            </div>
          )}
        </div>
      </div>,
      document.body)}

      <button
        ref={btnRef}
        onClick={hover.togglePin}
        {...hover.triggerProps}
        aria-label="Admin Tools"
        data-testid="admin-tools-square"
        // DP60 Part 0d — inline, this is a .km-railbox like every other box in
        // the rail: same size, same border, same spacing. No exceptions.
        className={inline
          ? `km-railbox pointer-events-auto ${expanded ? 'km-railbox--active' : ''}`
          : `pointer-events-auto w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200 cursor-pointer ${
              expanded
                ? 'ring-2 ring-[#BC5D41] ring-offset-2 shadow-lg'
                : darkMode
                  ? 'ring-1 ring-amber-900/40 shadow-md hover:ring-[#BC5D41]'
                  : 'ring-1 ring-amber-200 shadow-md hover:ring-[#BC5D41]'
            } ${darkMode ? 'bg-[#1A1A1A] ring-offset-[#0D0D0D]' : 'bg-white ring-offset-white'}`}
      >
        <Shield className={`w-4 h-4 ${darkMode ? 'text-amber-500' : 'text-amber-600'}`} />
      </button>
    </div>
  );
};

export default AdminToolsSquare;
